import { NSARouterMakerResult } from 'namirasoft-account-react';
import { EncodingOperation } from 'namirasoft-core';
import { UserFullRow } from 'namirasoft-password';
import { EncryptionOperation, NSBox, NSBoxString, NSButton, NSButtonBlue, NSLoading, NSPanel, NSSpace, NSSpaceSizeType } from 'namirasoft-site-react';
import { Component, createRef } from 'react';
import PasswordForm from '../components/PasswordForm';
import { PasswordLayout } from '../PasswordLayout';
import { PasswordRouterProps } from '../PasswordRouterProps';
import { PasswordQuestion } from '../PasswordQuestion';
import { Storage } from '../Storage';

export interface PasswordRecoveryPageProps extends NSARouterMakerResult, PasswordRouterProps
{
    user: UserFullRow;
    loadUser: () => Promise<void>;
}

export interface PasswordRecoveryPageState
{
    all_questions: string[];
    selected_questions: { id: number, text: string }[];
    ciphered_answers: string[];
    selected_set_index: number;
    master_key: string | null;
    loading: boolean;
}

export class PasswordRecoveryPage extends Component<PasswordRecoveryPageProps, PasswordRecoveryPageState>
{
    private PasswordForm_Ref = createRef<PasswordForm>();
    private NSBoxString_Refs = PasswordQuestion.SETS[0].map(() => createRef<NSBoxString>());

    constructor(props: PasswordRecoveryPageProps)
    {
        super(props);
        this.state = {
            all_questions: [],
            selected_questions: [],
            ciphered_answers: [],
            selected_set_index: -1,
            master_key: null,
            loading: true,
        }
        this.loadAllQuestions = this.loadAllQuestions.bind(this);
        this.selectRandomQuestions = this.selectRandomQuestions.bind(this);
        this.verifyAnswers = this.verifyAnswers.bind(this);
        this.changePassword = this.changePassword.bind(this);
    }

    override componentDidMount(): void
    {
        this.loadAllQuestions();
    }

    async loadAllQuestions()
    {
        try
        {
            let response = await this.props.servers.password.question.List();
            this.setState(
                () => ({
                    all_questions: response.questions,
                    ciphered_answers: response.ciphers,
                    loading: false
                }),
                this.selectRandomQuestions
            );
        }
        catch
        {
            // The server already reported the error through the notifier.
        }
    }

    selectRandomQuestions()
    {
        let random_set_index = Math.floor(Math.random() * PasswordQuestion.SETS.length);
        let selected_set = PasswordQuestion.SETS[random_set_index];
        let selected_questions = selected_set.map((index) => ({
            id: index + 1,
            text: this.state.all_questions[index]
        }));

        this.setState(() => ({
            selected_questions,
            selected_set_index: random_set_index
        }));
    }

    async verifyAnswers(onFinished: () => void)
    {
        try
        {
            let concatenated_answers = this.NSBoxString_Refs.reduce((acc, ref) => acc + PasswordQuestion.normalizeAnswer(ref.current?.getValue() ?? ""), "");
            let encoded_answers = EncodingOperation.Base64Encode(concatenated_answers);
            let master_key = await EncryptionOperation.AES256GCMDecrypt(encoded_answers, this.state.ciphered_answers[this.state.selected_set_index]) as string;
            this.setState(() => ({ master_key }), onFinished);
        }
        catch (error: any)
        {
            if (!error.message)
                this.props.notifier.onError("Some of your answers are incorrect.");
            else
                this.props.notifier.onError(error as Error);
            onFinished();
        }
    }

    async changePassword(onFinished: () => void)
    {
        let ciphered_master_key: string;
        try
        {
            let passwords = this.PasswordForm_Ref.current?.getPasswords();

            if (!passwords)
            {
                onFinished();
                return;
            }

            let encoded_password = EncodingOperation.Base64Encode(passwords.password);
            ciphered_master_key = await EncryptionOperation.AES256GCMEncrypt(encoded_password, this.state.master_key);
        }
        catch (error)
        {
            // Local input/encryption errors are not reported by the server, so notify here.
            this.props.notifier.onError(error as Error);
            onFinished();
            return;
        }

        try
        {
            await this.props.servers.password.user.Set({ cipher: ciphered_master_key });
            await Storage.delMasterKey(this.props.user.id);
            Storage.delUser(this.props.user.id);
            this.props.notifier.onSuccess("Your password has been updated successfully.");
            await this.props.loadUser();
            this.props.url.redirect("/", {});
            onFinished();
        }
        catch
        {
            // The server already reported the error through the notifier.
            onFinished();
        }
    }

    override render()
    {
        return (
            <PasswordLayout
                {...this.props}
                action={{ 
                    title: "Recover Master Password",
                    description: 'Regain access when you have forgotten your Master Password by answering your security questions. If you cannot answer the set shown, you can click on "Change questions" to switch to a different set of five (from the fifteen you answered at setup) and try those instead.'
                }}
            >
                <>
                    {
                        this.state.loading &&
                        <NSLoading />
                    }
                    {
                        !this.state.loading && !this.state.master_key &&
                        <NSPanel classList={["mx-auto"]}>
                            {
                                this.state.selected_questions.map((question, index) => (
                                    <NSBoxString
                                        key={question.id}
                                        ref={this.NSBoxString_Refs[index]}
                                        title={`${index + 1}. ${question.text}`}
                                        placeholder="Please enter your answer"
                                        required
                                        style={{ width: NSBox.width.triple }}
                                    />
                                ))
                            }
                            <NSSpace size={NSSpaceSizeType.MICRO} />
                            <NSButtonBlue
                                title="Apply"
                                onClick={{ action: this.verifyAnswers }}
                            />
                            <NSButton
                                title="Change Questions"
                                onClick={{
                                    action: this.selectRandomQuestions,
                                    showLoading: false
                                }}
                                icon={{
                                    src: "https://static.namirasoft.com/image/concept/refresh/blue.svg",
                                    alt: "refresh"
                                }}
                            />
                        </NSPanel>
                    }
                    {
                        !this.state.loading && this.state.master_key &&
                        <NSPanel classList={["mx-auto"]}>
                            <PasswordForm
                                ref={this.PasswordForm_Ref}
                                {...this.props}
                            />
                            <NSButtonBlue
                                title={"Apply"}
                                onClick={{ action: this.changePassword }}
                            // 
                            />
                        </NSPanel>
                    }
                </>
            </PasswordLayout>
        )
    }
}

export default PasswordRecoveryPage;